/* A skill folder on its way in: the rules the browser can know before the server's
   checker sees it, SKILL.md built from the editor's fields and read back into them, and
   the folder as the multipart body `POST /skills` takes (tasks/custom-skills/01-design-mock.md).
   Everything else about a folder is the server's to judge. */

import { DESCRIPTION_MAX, NAME_MAX, NAME_PATTERN, SKILL_CODES } from "@/shared/lib/skill-rules";

import { splitFrontmatter } from "./catalog";

import type { ApiError } from "@/shared/api/errors";
import type { SkillProblem } from "@/shared/types";

export function nameIssue(name: string): string | null {
  if (!name) return "A skill needs a name.";
  if (name.length > NAME_MAX) return `At most ${NAME_MAX} characters.`;
  if (!NAME_PATTERN.test(name)) return "Lowercase letters, digits and single hyphens between them.";
  return null;
}

/** Characters as the server counts them: code points, so an emoji is one. */
export const characters = (text: string) => [...text].length;

export function descriptionIssue(description: string): string | null {
  if (!description.trim()) return "Say what it does and when to use it: the agent picks a skill by this alone.";
  if (characters(description) > DESCRIPTION_MAX) return `At most ${DESCRIPTION_MAX.toLocaleString()} characters.`;
  return null;
}

/** One file of a folder being written: text is edited in place, anything else is kept as bytes. */
export interface DraftFile {
  path: string;
  text: string | null;
  blob: Blob | null;
  size: number;
}

export const textFile = (path: string, text: string): DraftFile => ({ path, text, blob: null, size: new Blob([text]).size });

/** A file's text when it is UTF-8 without a NUL byte — the server's rule for text
    (backend/modules/skills/business/files.py) — kept exactly, a byte-order mark included, so it
    goes back up as the same bytes. Anything else is null: keep the bytes. */
export async function readText(blob: Blob): Promise<string | null> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  if (bytes.includes(0)) return null;
  try {
    return new TextDecoder("utf-8", { fatal: true, ignoreBOM: true }).decode(bytes);
  } catch {
    return null;
  }
}

/** SKILL.md's fields apart: the name and description the editor shows as inputs, and every
    other frontmatter entry kept as written. */
export interface SkillMd {
  name: string;
  description: string;
  /** Frontmatter entries besides name and description (license, metadata …), as written. */
  extra: string;
  body: string;
}

/* The frontmatter's top-level entries: a key's line and every line under it that is blank,
   indented or a `- ` item — a block scalar, a folded line, a nested map or list stays whole.
   Any other line at the margin starts the next entry (a comment there stands alone). */
function entries(frontmatter: string): { key: string | null; text: string }[] {
  const out: { key: string | null; text: string }[] = [];
  for (const line of frontmatter.split(/\r?\n/)) {
    const last = out[out.length - 1];
    if (last && /^(\s|-(\s|$)|$)/.test(line)) last.text += `\n${line}`;
    else out.push({ key: line.match(/^(["']?)([^"'#\s][^"':]*?)\1\s*:/)?.[2] ?? null, text: line });
  }
  return out;
}

/** SKILL.md taken apart. Its name and description are YAML the server reads (PyYAML), not
    something to guess at here: an edit takes them from the skill, a SKILL.md uploaded into the
    editor from `POST /skills/check`. Every other entry is kept exactly as written. */
export function splitSkillMd(text: string): Pick<SkillMd, "extra" | "body"> {
  const { frontmatter, body } = splitFrontmatter(text);
  const kept = entries(frontmatter ?? "").filter((e) => e.key !== "name" && e.key !== "description");
  return { extra: kept.map((e) => e.text).join("\n").trim(), body: body.replace(/^\r?\n/, "") };
}

/* JSON's quoting is YAML's double-quoted style, except that YAML reads U+0085, U+2028 and
   U+2029 as line breaks and refuses DEL, the C1 controls and U+FFFE/FFFF written as they are. */
const YAML_UNSAFE = /[\u007f-\u009f\u2028\u2029\ufffe\uffff]/g;
const quoted = (text: string) =>
  JSON.stringify(text).replace(YAML_UNSAFE, (c) => `\\u${c.charCodeAt(0).toString(16).padStart(4, "0")}`);

/** The file the server reads: the frontmatter first, the description quoted so any text is valid YAML. */
export function buildSkillMd({ name, description, extra, body }: SkillMd): string {
  const head = [`name: ${name}`, `description: ${quoted(description)}`, extra].filter(Boolean).join("\n");
  return `---\n${head}\n---\n\n${body}`;
}

export const SKILL_MD_TEMPLATE: SkillMd = {
  name: "",
  description: "",
  extra: "",
  body: `# Title

What this skill is for, in a sentence.

## When to use it

- The kinds of task that should load this skill.

## Steps

1. The first thing to do.
2. The next.

## Output

What the finished work looks like.
`,
};

/* ------------------------------ on the wire ------------------------------ */

/* What every skill leaves out, as the server's checker does (backend/modules/skills/business/folder.py
   `is_kept`): git's and Python's own files, an operating system's, skills.sh's metadata. */
const LEFT_OUT_DIRS = new Set([".git", "__pycache__", "__pypackages__", "__MACOSX"]);
const LEFT_OUT_FILES = new Set(["metadata.json", ".DS_Store", "Thumbs.db", "desktop.ini"]);

export function isKept(path: string): boolean {
  const parts = path.split("/");
  return !LEFT_OUT_FILES.has(parts[parts.length - 1]) && !parts.slice(0, -1).some((d) => LEFT_OUT_DIRS.has(d));
}

/** The API takes a request body of at most 10 MB (core/asgi.MAX_BODY_BYTES): a bigger folder goes zipped. */
export const MAX_SEND = 10 * 1024 * 1024;

export const tooBigToSend = (files: { size: number }[]) => files.reduce((sum, f) => sum + f.size, 0) > MAX_SEND;

/** The refusal's problems: a 422 whose `details` are every problem the checker found. */
export const problemsOf = (error: ApiError | null): SkillProblem[] =>
  error?.code === SKILL_CODES.refused ? ((error.details ?? []) as unknown as SkillProblem[]) : [];

/** Each file a `files` part with its path in the folder beside it as `paths`, in step — the
    shape `POST /skills` and `/skills/check` read (the knowledge upload's too). */
export function folderForm(files: { path: string; body: Blob }[], fields: Record<string, string> = {}): FormData {
  const form = new FormData();
  for (const f of files) {
    form.append("files", f.body, f.path.split("/").pop());
    form.append("paths", f.path);
  }
  for (const [k, v] of Object.entries(fields)) form.append(k, v);
  return form;
}

export const draftForm = (files: DraftFile[], fields: Record<string, string> = {}) =>
  folderForm(files.map((f) => ({ path: f.path, body: f.blob ?? new Blob([f.text ?? ""], { type: "text/plain" }) })), fields);

/** A picked or dropped thing, with the path it has inside what was picked. */
export interface Picked {
  path: string;
  body: File;
}

/** Files from an `<input type="file">` — a folder picker keeps each file's place in it. */
export const fromInput = (list: FileList): Picked[] =>
  Array.from(list).map((file) => ({ path: file.webkitRelativePath || file.name, body: file }));

/* A dropped folder is walked entry by entry: `dataTransfer.files` holds only its top level. */
async function walk(entry: FileSystemEntry, into: Picked[]): Promise<void> {
  if (entry.isFile) {
    const file = await new Promise<File>((resolve, reject) => (entry as FileSystemFileEntry).file(resolve, reject));
    into.push({ path: entry.fullPath.replace(/^\//, ""), body: file });
    return;
  }
  const reader = (entry as FileSystemDirectoryEntry).createReader();
  // readEntries hands out a batch at a time (100 in Chrome) until it returns none
  for (;;) {
    const batch = await new Promise<FileSystemEntry[]>((resolve, reject) => reader.readEntries(resolve, reject));
    if (!batch.length) return;
    for (const child of batch) await walk(child, into);
  }
}

export async function fromDrop(data: DataTransfer): Promise<Picked[]> {
  const entries = Array.from(data.items)
    .map((item) => item.webkitGetAsEntry?.())
    .filter((e): e is FileSystemEntry => !!e);
  if (!entries.length) return fromInput(data.files);
  const picked: Picked[] = [];
  for (const entry of entries) await walk(entry, picked);
  return picked;
}
